import { createTrackingConsentBanner } from './banner';
import { buildTrackingConsentCookieName, readTrackingConsentDecision, writeTrackingConsentDecision } from './cookie';

import type {
  EffectiveTrackingConsentPolicy,
  StoredTrackingConsentDecision,
  TrackingConsentDecision,
  TrackingConsentManager,
  TrackingConsentScope,
  TrackingConsentSettingsButtonPosition,
  TrackingConsentSettingsButtonVariant
} from './types';

const SAVE_ERROR = 'We could not save your privacy choice. Please try again.';

type CreateTrackingConsentManagerOptions = {
  policy: EffectiveTrackingConsentPolicy;
  scope: TrackingConsentScope;
  document: Document;
  location: Location;
  now?: () => Date;
  showSettingsButton?: boolean;
  settingsButtonPosition?: TrackingConsentSettingsButtonPosition;
  settingsButtonVariant?: TrackingConsentSettingsButtonVariant;
};

export const createTrackingConsentManager = ({
  policy,
  scope,
  document,
  location,
  now = () => new Date(),
  showSettingsButton = false,
  settingsButtonPosition = 'bottom-right',
  settingsButtonVariant = 'text'
}: CreateTrackingConsentManagerOptions): TrackingConsentManager => {
  let decision: TrackingConsentDecision = policy.enabled
    ? readTrackingConsentDecision({
        document,
        scope,
        policyVersion: policy.policy_version,
        preferenceLifetimeDays: policy.preference_lifetime_days,
        now
      })
    : 'unknown';
  let saveError: string | null = null;
  let bannerController: ReturnType<typeof createTrackingConsentBanner> | null = null;

  const ensureBanner = (): ReturnType<typeof createTrackingConsentBanner> => {
    if (!bannerController) {
      bannerController = createTrackingConsentBanner({
        banner: policy.banner,
        document,
        id: buildTrackingConsentCookieName(scope),
        initialError: saveError,
        showSettingsButton,
        settingsButtonPosition,
        settingsButtonVariant,
        onAllow: () => {
          save('allowed');
        },
        onReject: () => {
          save('rejected');
        }
      });
    }

    return bannerController;
  };

  const save = (nextDecision: StoredTrackingConsentDecision): boolean => {
    if (!policy.enabled) {
      return false;
    }

    const saved = writeTrackingConsentDecision({
      document,
      location,
      scope,
      decision: nextDecision,
      policyVersion: policy.policy_version,
      preferenceLifetimeDays: policy.preference_lifetime_days,
      now
    });

    if (!saved) {
      decision = 'unknown';
      saveError = SAVE_ERROR;
      bannerController?.showError(SAVE_ERROR);
      return false;
    }

    decision = nextDecision;
    saveError = null;
    location.reload();
    return true;
  };

  return {
    get decision(): TrackingConsentDecision {
      return decision;
    },
    get saveError(): string | null {
      return saveError;
    },
    canTrack: () => !policy.enabled || decision === 'allowed',
    mountBanner: () => {
      if (!policy.enabled) {
        return;
      }

      const controller = ensureBanner();
      if (decision === 'unknown') {
        controller.open();
      }
    },
    openSettings: () => {
      if (policy.enabled) {
        ensureBanner().open();
      }
    },
    allow: () => save('allowed'),
    reject: () => save('rejected'),
    destroy: () => {
      bannerController?.destroy();
      bannerController = null;
    }
  };
};
